import React, { useCallback, useEffect, useState, useMemo } from "react";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Paper,
    Stack, Tooltip, Typography, TextField,
} from "@mui/material";
import AddRoundedIcon    from "@mui/icons-material/AddRounded";
import EditRoundedIcon   from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ActionButton from "../../../component/common/ActionButton";
import { channelApi } from "../api/channelApi";
import { notify } from "../../../util/toast";
import { runApiAction } from "../../../util/apiFeedback";
import { applyRangeEndChange, applyRangeStartChange } from "../../../util/dateUtils";
import dayjs          from "dayjs";
import BaseCalendar   from "../../../component/calendar/BaseCalendar";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";
import ComboBox from "../../../component/common/ComboBox";

const emptyForm = () => ({ title: "", description: "", startAt: "", endAt: "", taskId: "" });

function scheduleOnDay(schedule, day) {
    if (!schedule.startAt && !schedule.endAt) return false;
    const start = schedule.startAt ? dayjs(schedule.startAt).startOf("day") : null;
    const end = schedule.endAt ? dayjs(schedule.endAt).endOf("day") : start;
    if (!start) return false;
    const d = day.startOf("day");
    return !d.isBefore(start) && !d.isAfter(end);
}

function ScheduleCard({ schedule, onEdit, onDelete }) {
    return (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" alignItems="flex-start">
                <Box sx={{
                    width: 4, borderRadius: 1.5,
                    bgcolor: schedule.linkedTask ? "#2563eb" : "#2563eb",
                    mr: 1.5, alignSelf: "stretch", minHeight: 40,
                }} />
                <Box flex={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{schedule.title}</Typography>
                        {/* 수정·삭제 권한은 같다(작성자 본인 또는 채널 관리자) — 최종 판정은 서버가 하고,
                            여기 버튼은 그 규칙을 화면에 드러내는 역할이다. */}
                        <Stack direction="row" gap={0.2} sx={{ flexShrink: 0 }}>
                            <Tooltip title="수정">
                                <IconButton size="small" onClick={() => onEdit(schedule)}>
                                    <EditRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="삭제">
                                <IconButton size="small" onClick={() => onDelete(schedule.id)}>
                                    <DeleteRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.3 }}>
                        {schedule.startAt ? dayjs(schedule.startAt).format("YYYY.MM.DD HH:mm") : ""}
                        {schedule.endAt ? ` ~ ${dayjs(schedule.endAt).format("YYYY.MM.DD HH:mm")}` : ""}
                    </Typography>
                    {schedule.description && (
                        <Typography sx={{ fontSize: 13, mt: 0.5, color: "text.secondary" }}>{schedule.description}</Typography>
                    )}
                    {schedule.linkedTask && (
                        <Typography sx={{ fontSize: 12, mt: 0.5, color: "#2563eb" }}>
                            업무 연동: {schedule.linkedTask.title}
                        </Typography>
                    )}
                    {schedule.linkedTask?.assignee && (
                        <Typography sx={{ fontSize: 12, mt: 0.2, color: "#64748b" }}>
                            담당자: {schedule.linkedTask.assignee.name}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
}

export default function ScheduleTab({ channelId, tasks = [], members = [],
                                     /** 조회 범위 — {} (전체) | { folderId } | { onlyUnfiled: true } */
                                     folderScope = {},
                                     /** 새 일정의 기본 폴더 — '전체'·'미분류' 를 보고 있으면 null */
                                     composeFolderId = null }) {
    /**
     * 폴더 범위를 useEffect 의존성으로 쓰기 위한 문자열 키 —
     * folderScope 는 매 렌더 새 객체라 그대로 넣으면 무한 재조회가 된다.
     */
    const folderKey = JSON.stringify(folderScope);

    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [editingId, setEditingId] = useState(null);   // null = 추가, 값 있으면 그 일정 수정
    const [anchor, setAnchor] = useState(dayjs());
    const [selectedDay, setSelectedDay] = useState(null);
    const [filterMemberId, setFilterMemberId] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const res = await channelApi.getSchedules(channelId, folderScope);
            setSchedules(Array.isArray(res) ? res : (res?.schedules ?? []));
        } catch {
            notify("일정 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    // 폴더를 바꾸면 다시 읽는다 — 이전 폴더의 일정이 남아 있으면 어느 폴더를 보는지 알 수 없다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [channelId, folderKey]);

    const handleCreate = () => {
        if (!form.title.trim()) { notify("제목을 입력하세요", "error"); return; }
        return runApiAction({
            request: () => channelApi.createSchedule(channelId, {
                // 보고 있던 폴더가 기본값 — '전체'·'미분류' 에서 만들면 미분류로 들어간다.
                folderId: composeFolderId ?? null,
                title: form.title,
                description: form.description || null,
                startAt: form.startAt || null,
                endAt: form.endAt || null,
                taskId: form.taskId ? Number(form.taskId) : null,
            }),
            successMsg: "일정이 추가되었습니다",
            errorMsg: "일정 추가 실패",
            onSuccess: load,
            closeOnSuccess: closeDialog,   // 폼 초기화까지 한곳에서 — 다음 열 때 값이 남지 않게
        });
    };

    /** 수정 시작 — 카드 값을 폼에 실어 같은 다이얼로그를 연다. datetime-local 은 초를 못 받으므로 16자로 자른다. */
    const openEdit = (schedule) => {
        setEditingId(schedule.id);
        setForm({
            title: schedule.title || "",
            description: schedule.description || "",
            startAt: schedule.startAt ? String(schedule.startAt).slice(0, 16) : "",
            endAt: schedule.endAt ? String(schedule.endAt).slice(0, 16) : "",
            taskId: schedule.linkedTask?.id ?? "",
        });
        setOpen(true);
    };

    /** 닫을 때 편집 상태를 반드시 지운다 — 남아 있으면 다음 [일정 추가]가 수정 모드로 열린다. */
    const closeDialog = () => {
        setOpen(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const handleUpdate = () => {
        if (!form.title.trim()) { notify("제목을 입력하세요", "error"); return; }
        return runApiAction({
            request: () => channelApi.updateSchedule(editingId, {
                title: form.title,
                description: form.description || null,
                startAt: form.startAt || null,
                endAt: form.endAt || null,
                taskId: form.taskId ? Number(form.taskId) : null,
            }),
            successMsg: "일정이 수정되었습니다",
            errorMsg: "일정 수정 실패",
            onSuccess: load,
            closeOnSuccess: closeDialog,
        });
    };

    const handleDelete = async (scheduleId) => {
        if (!(await confirmDialog({ message: "일정을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteSchedule(scheduleId),
            errorMsg: "일정 삭제 실패",
            onSuccess: load,
        });
    };

    const filteredSchedules = useMemo(() => {
        if (!filterMemberId) return schedules;
        return schedules.filter(s => s.linkedTask?.assignee?.id === Number(filterMemberId));
    }, [schedules, filterMemberId]);

    const selectedDaySchedules = useMemo(() => {
        if (!selectedDay) return [];
        return filteredSchedules.filter(s => scheduleOnDay(s, selectedDay));
    }, [selectedDay, filteredSchedules]);

    const handleDayClick = useCallback((day) => {
        setSelectedDay(prev => prev && day.isSame(prev, "day") ? null : day);
    }, []);

    /* 다중일 일정만 연속 바(spans)로, 단일일은 셀 배지로 */
    const isMultiDaySchedule = (s) =>
        !!s.startAt && !!s.endAt && dayjs(s.startAt).format("YYYY-MM-DD") !== dayjs(s.endAt).format("YYYY-MM-DD");
    const singleDaySchedules = filteredSchedules.filter((s) => !isMultiDaySchedule(s));
    const spans = filteredSchedules.filter(isMultiDaySchedule).map((s) => ({
        id: s.id,
        start: s.startAt,
        end: s.endAt,
        label: s.title,
        bg: "#dbeafe", bar: "#2563eb", text: "#2563eb",
        onClick: () => setSelectedDay(dayjs(s.startAt).startOf("day")),
    }));

    return (
        <Box>
            {/* 헤더 */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonthRoundedIcon color="primary" />
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>일정</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {members.length > 0 && (
                        <Box sx={{ width: 150 }}>
                            <ComboBox placeholder="담당자 · 전체"
                                options={[{ value: "", label: "전체" }, ...members.map(m => ({ value: m.employee.id, label: m.employee.name }))]}
                                value={filterMemberId}
                                onChange={v => setFilterMemberId(v)} />
                        </Box>
                    )}
                    <ActionButton variant="add"
                        startIcon={<AddRoundedIcon />}
                        // 편집 상태를 명시적으로 비우고 연다 — 수정하다 만 값이 새 일정에 딸려가지 않게.
                        onClick={() => { setEditingId(null); setForm(emptyForm()); setOpen(true); }}
                        label="일정 추가" />
                </Stack>
            </Stack>

            {loading ? (
                <LoadingDots />
            ) : (
                <Box>
                    {/* BaseCalendar: 네비 + 월간 그리드 */}
                    <BaseCalendar
                        anchor={anchor}
                        onAnchorChange={(d) => { setAnchor(d); setSelectedDay(null); }}
                        view="month"
                        supportedViews={["month"]}
                        cellHeight={82}
                        selectedDay={selectedDay}
                        onDayClick={handleDayClick}
                        spans={spans}
                        renderDayBadges={(day) => {
                            const daySchedules = singleDaySchedules.filter(s => scheduleOnDay(s, day));
                            return (
                                <Stack spacing={0.2}>
                                    {daySchedules.slice(0, 2).map(s => (
                                        <Box key={s.id} sx={{
                                            px: 0.5, py: 0.15, borderRadius: 0.5,
                                            bgcolor: s.linkedTask ? "#dbeafe" : "#dbeafe",
                                            borderLeft: `2px solid ${s.linkedTask ? "#2563eb" : "#2563eb"}`,
                                        }}>
                                            <Typography sx={{
                                                fontSize: 10, fontWeight: 600, lineHeight: 1.3,
                                                color: s.linkedTask ? "#2563eb" : "#2563eb",
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }}>
                                                {s.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {daySchedules.length > 2 && (
                                        <Typography sx={{ fontSize: 10, color: "#94a3b8", pl: 0.3 }}>
                                            +{daySchedules.length - 2}개 더
                                        </Typography>
                                    )}
                                </Stack>
                            );
                        }}
                    />

                    {/* 선택된 날 일정 상세 */}
                    {selectedDay && (
                        <Box mt={2}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5, color: "#374151" }}>
                                {selectedDay.format("M월 D일 (ddd)")} 일정
                                <Box component="span" sx={{ ml: 1, fontSize: 13, color: "#2563eb", fontWeight: 600 }}>
                                    {selectedDaySchedules.length}건
                                </Box>
                            </Typography>
                            {selectedDaySchedules.length === 0 ? (
                                <Box sx={{ textAlign: "center", py: 3, color: "#94a3b8" }}>
                                    <Typography sx={{ fontSize: 13 }}>이 날 일정이 없습니다.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {selectedDaySchedules.map(s => (
                                        <ScheduleCard key={s.id} schedule={s} onEdit={openEdit} onDelete={handleDelete} />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {/* 날짜 미선택 시 전체 일정 */}
                    {!selectedDay && (
                        <Box mt={2}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5, color: "#374151" }}>
                                {anchor.format("M월")} 전체 일정
                                <Box component="span" sx={{ ml: 1, fontSize: 13, color: "#2563eb", fontWeight: 600 }}>
                                    {filteredSchedules.filter(s => {
                                        const start = s.startAt ? dayjs(s.startAt) : null;
                                        const end = s.endAt ? dayjs(s.endAt) : start;
                                        if (!start) return false;
                                        const mStart = anchor.startOf("month");
                                        const mEnd = anchor.endOf("month");
                                        return start.isBefore(mEnd) && (!end || end.isAfter(mStart));
                                    }).length}건
                                </Box>
                            </Typography>
                            {filteredSchedules.length === 0 ? (
                                <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                                    <Typography sx={{ fontSize: 13 }}>등록된 일정이 없습니다.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {filteredSchedules
                                        .filter(s => {
                                            const start = s.startAt ? dayjs(s.startAt) : null;
                                            const end = s.endAt ? dayjs(s.endAt) : start;
                                            if (!start) return true;
                                            const mStart = anchor.startOf("month");
                                            const mEnd = anchor.endOf("month");
                                            return start.isBefore(mEnd) && (!end || end.isAfter(mStart));
                                        })
                                        .sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)))
                                        .map(s => (
                                            <ScheduleCard key={s.id} schedule={s} onEdit={openEdit} onDelete={handleDelete} />
                                        ))
                                    }
                                </Stack>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* 일정 추가·수정 다이얼로그 — editingId 유무로 갈린다(입력 항목이 같아 폼을 나눌 이유가 없다) */}
            <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? "일정 수정" : "일정 추가"}</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={2}>
                        <FormField label="제목" required>
                            <TextField size="small" value={form.title}
                                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                                placeholder="제목을 입력하세요" fullWidth />
                        </FormField>
                        <FormField label="설명">
                            <TextField size="small" value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="설명을 입력하세요" multiline rows={2} fullWidth />
                        </FormField>
                        <FormField label="시작일시" required>
                            <TextField type="datetime-local" size="small" value={form.startAt}
                                onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startAt", endKey: "endAt" }))} fullWidth />
                        </FormField>
                        <FormField label="종료일시" required>
                            <TextField type="datetime-local" size="small" value={form.endAt}
                                onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startAt", endKey: "endAt" }))} fullWidth />
                        </FormField>
                        {tasks.length > 0 && (
                            <FormField label="업무 연동">
                                <ComboBox clearable={false} placeholder="업무 연동"
                                    options={[{ value: "", label: "없음" }, ...tasks.map(t => ({ value: t.id, label: t.title }))]}
                                    value={form.taskId}
                                    onChange={v => setForm(p => ({ ...p, taskId: v }))} />
                            </FormField>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <ActionButton variant="cancel" onClick={closeDialog} />
                    {editingId
                        ? <ActionButton variant="add" onClick={handleUpdate} label="저장" />
                        : <ActionButton variant="add" onClick={handleCreate} />}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
